export const componentsDefinition = `
/* Rules
1. IObject["$"] & {...} means: Take ALL properties from IObject.$, then ADD {...} properties. Include everything from both sides.
   Example: IPlayerLabel.$ has all from IObject.$ (id, player, autoScaling) PLUS IPlayerObject.$ additions (label, clickEventName?, clickEventParam?) PLUS its own (player: "3241")

2. Data: IObject["Data"] & [...] means: Data is an array that merges all elements from IObject.Data with all elements of [...].
   Example: Data: IObject["Data"] & [IDataEventCommands & IDataAnimation & IDataStat] means:
   Data: [IDataLayout, IDataEffects & IDataEventCommands & IDataAnimation & IDataStat]

   For IPlayerLabel extending IPlayerObject:
   Data: [IDataLayout, IDataEffects & IDataEventCommands & IDataAnimation & IDataStat, {Label: [...]}]

3. Font: [{ $: IFont }] means: Font is an array with one element. That element is an object with a $ property containing all IFont properties.
   Example JSON: "Font": [{ "$": { "fontColor": "16777215", "fontSize": "24", "fontFamily": "Arial", "fontWeight": "normal", "fontStyle": "normal", "alignSelf": "center", "textAlign": "center", "textDecoration": "none", "wrap": "1", "autoScaling": "1" } }]


4. When building Json from the Interfaces, you Must make sure that the Json is Valid, and it follows interfaces rules!
*/

export type Bool = "0" | "1";
export type Decimal = string; // Decimal number represented as a string.
export type Color = string; // Color type for decimal color values. Default: "16777215"
export type Alpha = string; // Alpha/opacity value as decimal string (0.0 to 1.0)
export type Blur = string; // Blur value as string (0 to 100)



export interface IObject {
    $: {
        id: string;
        player: string;
        autoScaling: Bool; // Required. Default "1"
    };
    Data: [IDataLayout]; // Required
}


export interface IPlayerObject extends IObject {
    $: IObject["$"] & {
        label: string;
        clickEventName?: string;
        clickEventParam?: string;
    }
    Data: IObject["Data"] & [
        IDataEffects & IDataEventCommands & IDataAnimation & IDataStat
    ]
}





export interface IDataLayout {
    Layout?: [{
        $: {
            x?: Decimal;         // is used when autoScaling=="1"
            y?: Decimal;         // is used when autoScaling=="1"
            width?: Decimal;     // is used when autoScaling=="1"
            height?: Decimal;    // is used when autoScaling=="1"
            left?: Decimal;
            right?: Decimal;
            top?: Decimal;
            bottom?: Decimal;
            horizontalCenter?: Decimal;
            verticalCenter?: Decimal;
            rotation: Decimal;  // Default "0"
            locked?: string;
            invisible?: string;
        };
        Actions?: any;  // deprecated
    }];
}

export interface IDataEffects {
    Background?: [{
        $: {
            color: Color;
            alpha: Alpha;
        };
    }];
    Border?: [{
        $: {
            borderColor: Color;
            borderThickness: string;
        };
    }];
    Appearance?: [{
        $: {
            alpha: Alpha;
        };
    }];
    Glow?: [{
        $: {
            color: Color;
            blur: Blur;
            alpha: Alpha;
        };
    }];
    Blur?: [{
        $: {
            blurX: Blur;
        };
    }];
    Shadow?: [{
        $: {
            alpha: Alpha;
            angle: string;
            blur: Blur;
            color: Color;
            distance: string;
        };
    }];
}

export interface IDataProvider {
    DataProvider?: [{
        $: {
            eventName: string;
            source: string;
            metadata: string;
            url: string;
        };
    }];
}

export interface IPlayerEventCommand {
    $: {
        from: string;
        command: string;
    };
    Params?: any;
}


export interface IDataEventCommands {
    EventCommands?: [{
        EventCommand: IPlayerEventCommand[];
    }];
}

export interface IPlayerDataAnimation {
    In?: [IPlayerAnimationIn];
    Out?: [IPlayerAnimationOut];
    Play?: [IPlayerAnimationPlay];
    Touch?: [IPlayerAnimationTouch];
}

export interface IDataAnimation {
    Animation?: [IPlayerDataAnimation];
}

export interface IDataStat {
    Stat: [{
        $: {
            enabled: Bool;
            tag: string;
            field: string;
            minThreshold: string;
            maxThreshold: string;
        };
    }];
}








export interface IPlayerAnimationIn {
    $: {
        enabled: string;
        delay: string;
        duration: string;
        fadeFrq: string;
        moveFrq: string;
        moveDir: string;
        moveAngle: string;
        moveIntense: string;
        scaleFrq: string;
        rotateFrq: string;
        rotateType: string;
        rotations: string;
        blurFrq: string;
        blurValue: Blur;
        textTypingFrq: string;
    };
}

export interface IPlayerAnimationOut {
    $: {
        enabled: string;
        delay: string;
        duration: string;
        fadeFrq: string;
        moveFrq: string;
        moveDir: string;
        moveAngle: string;
        moveIntense: string;
        scaleFrq: string;
        rotateFrq: string;
        rotateType: string;
        rotations: string;
        blurFrq: string;
        blurValue: Blur;
        textTypingFrq: string;
    };
}

export interface IPlayerAnimationPlay {
    $: {
        enabled: string;
        duration: string;
        frequency: string;
        rotateFrq: string;
        rotateValue: string;
        scaleFrq: string;
        scaleValue: string;
        fadeFrq: string;
    };
}

export interface IPlayerAnimationTouch {
    $: {
        enabled: string;
        duration: string;
        scaleValue: string;
    };
}



export interface IPlayerResourceAspectRatio {
    $: {
        maintain: string;
    };
}

export interface IPlayerResourceSvg {
    Item?: IPlayerResourceSvgItem[];
}

export interface IPlayerResourceSvgItem {
    $: {
        path: string;
        fill: string;
        stroke: string;
    };
}


export declare interface IFont {
    fontColor: Color;
    fontSize: string;
    fontFamily: string;
    fontWeight: string;
    fontStyle: string;

    alignSelf: string;      // Default: "center"
    textAlign: string;      // Default: "center"
    textDecoration: string; // Default: "none"
    wrap: Bool;             // Default: "1"
    autoScaling: Bool;      // Default "1"
}



// components

export interface IPlayerScene extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3510";
        changelist_id: string;
        folder: string;
        accessKey: string;
        private_child: string;

        mimeType: string;
    };
    Data: IPlayerObject["Data"] & [
        {
            Scene: [{
                Players: [{
                    Player: IObject[];
                }];
            }];
        }
    ];
}

export interface IMainSceneObject extends IPlayerScene {
    Metadata: [{
        Kiosk: [{
            $: {
                mode: Bool;     // "1" mean use Kiosk mode. Default "0"
                key: string;    // Default ""
            };
        }];
    }];
}


export interface ILinkedScene extends IObject {
    $: IObject["$"] & {
        player: "3510";
        src: string;
    }
}



export interface IPlayerLabel extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3241";
    };
    Data: IPlayerObject["Data"] & [
        {
            Label: [{
                Font: [{ $: IFont }];
                Text: [string];
            }];
        }
    ];
}


export interface IPlayerMessage extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3245";
    };
    Data: IPlayerObject["Data"] & [
        {
            Label: [{
                Font: [{ $: IFont }];
            }];
        }
    ];
}


export interface IPlayerQr extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3430";
    };
    Data: IPlayerObject["Data"] & [
        {
            Text: [any];
        }
    ];
}



export interface IPlayerLabelQueue extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3242";
    };
    Data: IPlayerObject["Data"] & [
        {
            LabelQueue: [{
                $: {
                    duration: string;
                };
                Font: [{$: IFont}];
                TextList: [{
                    Text: Array<{
                        $: {
                            text: string;
                            duration?: string;
                        };
                    }>;
                }];
            }];
        }
    ];
}

export interface IPlayerClock extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3320";
    };
    Data: IPlayerObject["Data"] & [
        {
            Clock: [{
                $: {
                    clockFormat: string; // Default: "time"
                    clockMask: string; // Default: "L:NN A"
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerCountdown extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3322";
    };
    Data: IPlayerObject["Data"] & [
        {
            Countdown: [{
                $: {
                    formatText: string;
                    mode: string;
                    finalDate: string;
                    duration: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}


export interface IPlayerCamera extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3350";
    };
}


export interface IPlayerChart extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3400";
    };
}




export interface IPlayerButton extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7000";
    };
    Data: IPlayerObject["Data"] & [
        {
            Button: [{
                $: {
                    label: string;
                    fieldName: string;
                };
                Font: [ {$: IFont} ];
            }];
        }
    ];
}

export interface IPlayerCheck extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7010";
    };
    Data: IPlayerObject["Data"] & [
        {
            Check: [{
                $: {
                    label: string;
                    fieldName: string;
                    defaultValue: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerStepper extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7020";
    };
    Data: IPlayerObject["Data"] & [
        {
            Stepper: [{
                $: {
                    fieldName: string;
                    minimum: string;
                    maximum: string;
                    step: string;
                    defaultValue: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerRadio extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7030";
    };
    Data: IPlayerObject["Data"] & [
        {
            Radio: [{
                $: {
                    fieldName: string;
                    label: string;
                    value: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerSlider extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7060";
    };
    Data: IPlayerObject["Data"] & [
        {
            Slider: [{
                $: {
                    fieldName: string;
                    minimum: string;
                    maximum: string;
                    step: string;
                    defaultValue: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerSwitch extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7040";
    };
    Data: IPlayerObject["Data"] & [
        {
            Switch: [{
                $: {
                    fieldName: string;
                    defaultValue: string;
                    offLabel: string;
                    onLabel: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerTextInput extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7050";
    };
    Data: IPlayerObject["Data"] & [
        {
            TextInput: [{
                $: {
                    fieldName: string;
                    defaultValue: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}

export interface IPlayerImage extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3130";  // or "3140" - both map to ImageComponent
    };
    Data: IPlayerObject["Data"] & [
        {
            Resource: [IPlayerDataResource];
        }
    ];
}

export interface IPlayerVideo extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3100";
    };
    Data: IPlayerObject["Data"] & [
        {
            Resource: [IPlayerDataResource];
        }
    ];
}

export interface IPlayerMusic extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3145";
    };
    Data: IPlayerObject["Data"] & [
        {
            Resource: [IPlayerDataResource];
        }
    ];
}



export interface IPlayerMusicList extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4110";
    };
    Data: IPlayerObject["Data"] & [
        {
            Music: [{
                $: {
                    duration: string;
                };
                Page: Array<{
                    $: {
                        duration?: string;
                    };
                    Player: [IPlayerMusic];
                }>;
            }];
        }
    ];
}

export interface IPlayerDataResource {
    $: {
        resource: string;
    };
    AspectRatio: [IPlayerResourceAspectRatio];
    Video?: [IPlayerResourceVideo];
    Svg?: [IPlayerResourceSvg];
}

export interface IPlayerResourceVideo {
    $: {
        autoRewind: string;
        volume: string;
    };
}



export interface IPlayerExtImage extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3160";
    };
    Data: IPlayerObject["Data"] & [
        {
            LINK: [{
                $: {
                    src: string;
                };
                AspectRatio: [IPlayerResourceAspectRatio];
            }];
        }
    ];
}

export interface IPlayerExtVideo extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3150";
    };
    Data: IPlayerObject["Data"] & [
        {
            LINK: [{
                $: {
                    src: string;
                };
                AspectRatio: [IPlayerResourceAspectRatio];
                Video?: [IPlayerResourceVideo];
            }];
        }
    ];
}



export interface IPlayerExtApp extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3410";
    };
    Data: IPlayerObject["Data"] & [
        {
            ExtApp: [{
                $: {
                    appName64: string;
                };
            }];
        }
    ];
}



export interface IPlayerHtml extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3235";  // or "5500" - both map to HtmlComponent
    };
    Data: IPlayerObject["Data"] & [
        {
            HTML: [{
                $: {
                    src: string;
                    showScrolls: string;
                    useProxy?: string;
                    staticTexture?: string;
                };
            }];
        }
    ];
}


export interface IPlayerYouTube extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4600";
    };
    Data: IPlayerObject["Data"] & [
        {
            YouTube: [{
                $: {
                    listType: "manually" | "most_viewed" | "category" | "channel";   // Default: "manually"
                    listRegion: string;     // Default: "US"
                    category: string;       // Default: ""
                    channel: string;        // Default: ""
                    quality: string;        // Default: "default"
                    randomOrder: Bool;      // Default: "0"
                    volume: string;         // Default: "1"

                };
                VideoIdList: string[];      // Default: ["w0ztlIAYTCU\nAeJ9q45PfD0\nMo1lDsESD90"]. this is only 1 string at position 0 in the array, don't split it!
            }
            ];
        }
    ];
}



export interface IPlayerSchedule extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4104";
    };
    Data: IPlayerObject["Data"] & [
        {
            Schedule: [{
                $: {
                    id: string;
                };
            }];
        }
    ];
}





export interface IPlayerPlaylist extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4102";
    };
    Data: IPlayerObject["Data"] & [
        {
            Playlist: [{
                $: {
                    duration: string;
                };
                Page: Array<{
                    $: {
                        duration?: string;
                    };
                    Player: [IObject];
                }>;
            }];
        }
    ];
}



export interface IPlayerCollection extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4100";
    };
    Data: IPlayerObject["Data"] & [
        {
            Collection: [{
                $: {
                    useHotkeys: Bool;   // default "0"
                };
                Page: Array<{
                    $: {
                        page: string;
                    };
                    Player: [IObject];
                }>;
            }];
        }
    ];
}


export interface IPlayerLoader extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4103";
    };
}

export interface IPlayerHourlyBalancer extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4120";
    };
    Data: IPlayerObject["Data"] & [
        {
            HourlyBalancer: [{
                $: {
                    fillGaps: string;
                };
                Page: Array<{
                    $: {
                        repetitions: string;
                        duration: string;
                    };
                }>;
            }];
        }
    ];
}


export interface IPlayerJsonViewer extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4305";  // or "6010", "6022", "6500" - all map to JsonViewerComponent
    };
    Data: IPlayerObject["Data"] & [
            IDataProvider & {
            Json: [{
                $: {
                    providerType: string;
                    minRefreshTime: string;
                    refreshEnabled: string;
                    itemInterval: string;
                    volume: string;
                };
                Data: [{
                    $: {
                        token?: string;
                        id?: string;
                        key?: string;
                        board?: string;
                        path?: string;
                        user?: string;
                        screenName?: string;
                        unit?: string;
                        address?: string;
                        style?: string;
                        mode?: string;
                        startDate?: string;
                        endDate?: string;
                        before?: string;
                        after?: string;
                    };
                }];
                Player: [ILinkedScene];
            }
            ];
        }
    ];
}


export interface IPlayerJsonList extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4300";  // or "4330", "4400", "4500", "6000", "6020", "6030", etc.
    };
    Data: IPlayerObject["Data"] & [
            IDataProvider & {
            Json: [{
                $: {
                    providerType: string;
                    minRefreshTime: string;
                    refreshEnabled: string;
                    itemInterval: string;
                    volume: string;
                };
                Data: [{
                    $: {
                        token?: string;
                        id?: string;
                        key?: string;
                        board?: string;
                        path?: string;
                        user?: string;
                        screenName?: string;
                        unit?: string;
                        address?: string;
                        style?: string;
                        mode?: string;
                        startDate?: string;
                        endDate?: string;
                        before?: string;
                        after?: string;
                    };
                }];
                Player: [ILinkedScene];
            }
            ];
        }
    ];
}

export interface IPlayerAyreshare extends IPlayerJsonList {
    $: IPlayerJsonList["$"] & {
        player: "6300";  // or "6305"-"6350" for different social platforms
    };
}

export interface IPlayerCatalog extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3280";
    };
    Data: IPlayerObject["Data"] & [
        {
            Catalog: [{
                $: {
                };
                Categories?: [{
                    Category: Array<{
                        $: {
                            id: string;
                        };
                    }>;
                }];
                Player: [ILinkedScene];
            }];
        }
    ];
}


export interface IPlayerCatalogItem extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3270";
    };
    Data: IPlayerObject["Data"] & [
        {
            CatalogItem: [{
                $: {
                    field: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}



export interface IPlayerXml extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4200";
    };
    Data: IPlayerObject["Data"] & [
        {
            Xml: [{
                $: {
                    url: string;
                    itemsPath: string;
                };
                Player: [ILinkedScene];
            }];
        }
    ];
}


export interface IPlayerXmlItem extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4210";
    };
    Data: IPlayerObject["Data"] & [
        {
            XmlItem: [{
                $: {
                    fieldName: string;
                    fieldType: string;
                    maintainAspectRatio: string;
                    dateFormat: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}


export interface IPlayerJsonItem extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4310";  // or "4505"
    };
    Data: IPlayerObject["Data"] & [
        {
            XmlItem: [{
                $: {
                    fieldName: string;
                    fieldType: string;
                    maintainAspectRatio: string;
                    dateFormat: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}


export interface IPlayerStocks extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3338";
    };
    Data: IPlayerObject["Data"] & [
        {
            Stocks: [{
                $: {
                    displayMode: string;
                };
                Symbols: [string];
                Player: [ILinkedScene];
            }];
        }
    ];
}


export interface IPlayerStockItem extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3335";
    };
    Data: IPlayerObject["Data"] & [
        {
            StockItem: [{
                $: {
                    field: string;
                    negativeColor: Color;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}




export interface IPlayerCanva extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "5600";
    };
    Data: IPlayerObject["Data"] & [
        {
            Canva: [{
                $: {
                    embeddedLink: string;
                };
            }];
        }
    ];
}



export interface IPlayerPtt extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7100";
    };
    Data: IPlayerObject["Data"] & [
        {
            Ptt: [{
                $: {
                    label: string;
                    fieldName: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}


export interface IPlayerSkp extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7101";
    };
    Data: IPlayerObject["Data"] & [
        {
            Spk: [{
                $: {
                    label: string;
                    fieldName: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}



export interface IPlayerSerialPort extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "5000";
    };
    Data: IPlayerObject["Data"] & [
        {
            SerialPort: [{
                $: {
                    com: string;
                    baudRate: string;
                };
            }];
        }
    ];
}


export interface IPlayerRss extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3345";
    };
    Data: IPlayerObject["Data"] & [
        {
            Rss: [{
                $: {
                    minRefreshTime: string; // Default "30".
                    speed: string;          // Default "20".
                    rtl: Bool;              // Default "0"
                    vertical: Bool;         // Default "0"
                    url: string;            // Default: "http://rss.news.yahoo.com/rss/world"

                    itemInterval?: string;
                    displayMode?: string;
                    playVideoInFull?: string;
                    volume?: string;

                };
                Title?: [{
                    Font: [{$: IFont}];
                }];
                Description?: [{
                    Font: [{$: IFont}];
                }];
                CustomRss?: [{
                    $: {
                        itemInterval?: string;
                        displayMode?: string;
                        vertical?: string;
                        playVideoInFull?: string;
                        volume?: string;
                    };
                    Player: [ILinkedScene];
                }];
                rss?: [{
                    channel?: [{
                        item?: any[];
                    }];
                }];
            }];
        }
    ];
}


export interface IPlayerCustomRss extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3346";
    };
    Data: any;
}


export interface IPlayerRssItem extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3348";
    };
    Data: IPlayerObject["Data"] & [
        {
            RssItem: [{
                $: {
                    field: string;
                    maintainAspectRatio: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}



export interface IPlayerFlicking extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4101";
    };
    Data: IPlayerObject["Data"] & [
        {
            Flicking: [{
                $: {
                    vertical: string;
                };
                Page: Array<{
                    $obs: any;
                }>;
            }];
        }
    ];
}


// IPlayerStationlist is deprecated
export interface IPlayerStationlist extends IPlayerObject {
}


// IPlayerGps is deprecated
export interface IPlayerGps extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "7200";
    };
    Data: IPlayerObject["Data"] & [
        {
            Radio: [{
                $: {
                    fieldName: string;
                    label: string;
                    value: string;
                };
                Font: [{$: IFont}];
            }];
        }
    ];
}



// IPlayerCart is deprecated
export interface IPlayerCart extends IPlayerJsonList {
}


// IPlayerText is deprecated
export interface IPlayerText extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3240";  // RichTextComponent based on the Text data structure
    };
    Data: IPlayerObject["Data"] & [
        {
            Text: [any];
        }
    ];
}



// IPlayerKeyboard is deprecated
export interface IPlayerKeyboard extends IPlayerObject {
}


// IPlayerForm is deprecated
export interface IPlayerForm extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3600";
    };
}


// IPlayerFasterQ is deprecated
export interface IPlayerFasterQ extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "6100";
    };
}


// IPlayerLocationBase is deprecated
export interface IPlayerLocationBase extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "4105";
    };
}

// IPlayerPodcast is deprecated
export interface IPlayerPodcast extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3340";
    };
}

// IPlayerMap is deprecated
export interface IPlayerMap extends IPlayerObject {
}

// IPlayerNvr is deprecated
export interface IPlayerNvr extends IPlayerObject {
}


// IPlayerAdNet is deprecated
export interface IPlayerAdNet extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "3420";
    };
    Data: IPlayerObject["Data"] & [
        {
            AdNet: [any];
        }
    ];
}



// IPlayerKnx is deprecated
export interface IPlayerKnx extends IPlayerObject {
    $: IPlayerObject["$"] & {
        player: "5010";
    };
    Data: IPlayerObject["Data"] & [
        {
            Knx: [any];
        }
    ];
}


`;
